import { PageHeader } from "@/components/page";
import { generateMeetingMinutesData } from "@/hooks";
import { useRoleProtected } from "@/hooks/useRoleProtected";
import { useTenantStore } from "@/stores/tenantStore";
import { useNavigate, useParams } from "react-router-dom";
import { MeetingEditor } from "./meetingEditor/MeetingEditor";
import { useDisableTeam } from "@/components/page/disableTeam";
import { useUserStore } from "@/stores/userStore";
import { Button } from "@repo/ui/components/Base/Button";
import { useEffect, useMemo, useState } from "react";
import { differenceInHours } from "date-fns";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { InformationDialog } from "@repo/ui/components/Base/InformationDialog";
import {
  PublishConfirmation,
  type PublishConfirmationProps,
} from "@/components/dialog/PublishConfirmation";
import type { MinutesOrdering, User } from "@repo/types";
import { ChevronDownIcon, EyeIcon, PlayIcon } from "lucide-react";
import { AppUrlPath } from "@/lib/AppUrlPath";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { formatTZ } from "@repo/shared/utils";
import { MeetingMinutesDocument } from "@repo/ui/components/Base/MeetingMinutesDocument";
import { pdf } from "@react-pdf/renderer";
import { MeetingMinutesPreviewDialog } from "@/components/meetings/MeetingMinutesPreviewDialog";
import { CreateDocumentDialog } from "@/components/documents";
import { MeetingMinutesDialog } from "@/components/meetings/MeetingMinutesDialog";
import { pullUrlParams } from "@/lib/urlParams";
import { MeetingPreviewDialog } from "@/components/meetings/MeetingPreviewDialog";
import { MeetingRunModeDialog } from "@/components/meetings/MeetingRunModeDialog";
import { meetingService } from "@/services/meetingService";
import { ErrorsDialog } from "@/components/meetings/ErrorsDialog";
import { Delta } from "quill/core";
import { MeetingGenerateMinutesDialog } from "@/components/meetings/MeetingGenerateMinutesDialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { MeetingMinutesAIDialog } from "@/components/meetings/MeetingMinutesAIDialog";
import { ConfirmDialog } from "@repo/ui/components/Base/ConfirmDialog";
import { DocumentPreviewer } from "@/components/dialog/DocumentPreviewer";
import { useGetTeam } from "@/hooks/queryHooks/teamHooks";
import { useDocumentList } from "@/hooks/queryHooks/documentHooks";
import { useUserList } from "@/hooks/queryHooks/userHooks";
import { useGetMeeting } from "@/hooks/queryHooks/meetingHooks";
import { meetingRepo } from "@/repositories/meetingRepo";
import { agendaItemRepo } from "@/repositories/agendaItemRepo";

const editMeeting24hoursWarning = (
  <>
    Edits to this agenda within 24 hours of the advertised meeting time may
    violate the Pennsylvania Sunshine Act. Exceptions exist such as in the case
    of emergencies and certain non-substantive changes. Please consult with your
    solicitor if you have any questions regarding the applicability of the
    Sunshine Act to your proposed edits.
  </>
);

export const MeetingPage = () => {
  useDisableTeam();
  useRoleProtected("view");

  const params = useParams();
  const tenantId = params.tenantId ?? "";
  const meetingId = params.meetingId ?? "";

  const tenantData = useTenantStore((s) => s.tenant);
  const { data: meetingData } = useGetMeeting(tenantId ?? "", meetingId ?? "");
  const { data: teamData } = useGetTeam(tenantId, meetingData?.teamId ?? "");
  const { data: userList } = useUserList(tenantId);
  const { data: allDocuments } = useDocumentList(tenantData?.id ?? "", "org");

  const userMap = useMemo(
    () =>
      userList?.reduce(
        (map, user) => ((map[user.id] = user), map),
        Object.create(null) as Record<string, User>
      ),
    [userList]
  );

  const meetingHasMinutes = allDocuments?.some(
    (doc) => doc.minutesData?.meetingId === meetingId
  );

  const { user, activeTeam, hasMinRole } = useUserStore((s) => ({
    user: s.user,
    activeTeam: s.activeTeam,
    hasMinRole: s.hasMinRole,
  }));
  const userId = user?.id ?? "";

  const activeTeamId =
    activeTeam && activeTeam !== "org" ? activeTeam.id : "org";
  const teamMismatch = meetingData?.teamId != activeTeamId;

  const viewOnly = teamMismatch || !hasMinRole("edit");

  const { edit: initialEdit } = pullUrlParams(true);

  const meetingStartsLessThan24Hours =
    !!meetingData?.startTime &&
    differenceInHours(meetingData?.startTime, new Date()) <= 24;

  const [editing, setEditing] = useState(
    initialEdit && !viewOnly && !meetingStartsLessThan24Hours
  );
  const [loading, setLoading] = useState(false);
  const [loadingRunMeeting, setLoadingRunMeeting] = useState(false);
  const [errors, setErrors] = useState("");
  const [errorDialogTitle, setErrorDialogTitle] = useState("");

  useEffect(() => {
    if (!viewOnly && meetingData?.agenda?.agendaModuleIds.length === 0) {
      setEditing(true);
    }
  }, [viewOnly, meetingData?.agenda?.agendaModuleIds.length]);

  const navigate = useNavigate();

  const canViewConfidential =
    teamData?.teamMembers.some(
      (member) => member.userId === userId && member.class === "voting"
    ) ||
    (activeTeam !== "org" &&
      !!activeTeam?.id &&
      !!meetingData?.teamId &&
      activeTeam.id === meetingData.teamId &&
      hasMinRole("edit"));

  // determine if user can start meeting:
  // if they have edit privilege, or if they can vote on meeting
  const memberClass =
    activeTeam !== "org"
      ? activeTeam?.teamMembers.find((member) => member.userId === user?.id)
          ?.class
      : undefined;

  const canRunMeeting =
    hasMinRole("edit") || !memberClass || memberClass === "voting";

  const isEditing = !viewOnly && editing;

  // set up popups
  const [warningMeetingDialogOpen, setWarningMeetingDialogOpen] =
    useState(false);
  const [publishMeetingDialogOpen, setPublishMeetingDialogOpen] =
    useState(false);
  const [errorsDialogOpen, setErrorsDialogOpen] = useState(false);
  const [previewMinutesOpen, setPreviewMinutesOpen] = useState(false);
  const [meetingMinutesDocumentOpen, setMeetingMinutesDocumentOpen] =
    useState(false);
  const [editMinutesOpen, setEditMinutesOpen] = useState(false);
  const [minutesWarningOpen, setMinutesWarningOpen] = useState(false);
  const [meetingMinutesAIOpen, setMeetingMinutesAIOpen] = useState(false);
  const [previewPDFOpen, setPreviewPDFOpen] = useState(false);
  const [previewPublishedPDFOpen, setPreviewPublishedPDFOpen] = useState(false);
  const [meetingRunModeOpen, setMeetingRunModeOpen] = useState(false);
  const [confirmDeleteMinutesOpen, setConfirmDeleteMinutesOpen] =
    useState(false);

  // set up meeting minutes attachment
  const [minutesPDFFile, setMinutesPDFFile] = useState<File>();

  // set up minutes data handling
  const [minutesData, setMinutesData] = useState<null | Delta | string>(null);
  const [minutesKey, setMinutesKey] = useState(0);

  // callback handlers
  const handleEditCheck = useFutureCallback(() => {
    if (!editing && meetingStartsLessThan24Hours) {
      setWarningMeetingDialogOpen(true);
    } else {
      handleEdit();
    }
  });

  const handleEdit = useFutureCallback(() => {
    setEditing((editing) => !editing);
  });

  const handleOpenPublish = useFutureCallback(() => {
    setPublishMeetingDialogOpen(true);
  });

  const handlePublish = useFutureCallback((async (
    meetingId,
    { sendEmails }
  ) => {
    if (!tenantId || !meetingId) return;

    setLoading(true);
    const errors = await meetingService.publishMeeting({
      tenantId,
      meetingId,
      sendEmails,
    });
    setLoading(false);

    if (errors) {
      setErrorDialogTitle("Errors While Publishing Meeting");
      setErrors(
        "The document finished publishing, but the following warnings / errors occurred when publishing:\n\n" +
          errors
      );
      setErrorsDialogOpen(true);
    }
  }) satisfies PublishConfirmationProps["onConfirm"]);

  const handleRunMeeting = useFutureCallback(async () => {
    if (!tenantId || !meetingId || !meetingData) return;

    if (!meetingData.review) {
      handleStartRunMeeting("run");
    } else {
      setMeetingRunModeOpen(true);
    }
  });

  const handleStartRunMeeting = useFutureCallback(
    async (mode: "edit" | "run") => {
      if (!tenantId || !meetingId || !meetingData) return;

      setLoadingRunMeeting(true);

      for (const agendaModule of meetingData.agenda?.agendaModules ?? []) {
        for (const agendaItemData of agendaModule.agendaItems ?? []) {
          if (agendaItemData.review?.meetingId !== meetingId) {
            await agendaItemRepo.update(tenantId, agendaItemData.id, {
              review: { meetingId },
            });
          }
        }
      }

      setLoadingRunMeeting(false);

      // IMPORTANT:
      // clear cache for meeting so that refetch runs and gets items with new versions
      meetingRepo.invalidate({ id: meetingId });

      const appUrlPath = new AppUrlPath(tenantId);
      navigate(
        appUrlPath.getMeetingRunPath(meetingId) +
          (mode === "edit" ? "#noTimestamps=true" : "")
      );
    }
  );

  const handleOpenMeetingMinutes = useFutureCallback(
    async (order: MinutesOrdering = "timestamps") => {
      const minutesData = await generateMeetingMinutesData({
        tenantId,
        meetingId,
        order,
      });

      if (!minutesData) {
        console.error("Failed to retrieve or generate minutes data.");
        return;
      }

      setMinutesKey((key) => key + 1);
      setMinutesData(minutesData);
      setEditMinutesOpen(true);
    }
  );

  const handleCreateDocumentFromMinutes = useFutureCallback(async () => {
    if (!tenantData || !meetingData || !teamData) return;

    const meetingDate = formatTZ(
      meetingData.startTime,
      "yyyy-MM-dd",
      tenantData?.timeZone ?? "America/New_York"
    );
    const blob = await pdf(
      <MeetingMinutesDocument
        meeting={meetingData}
        team={teamData}
        letterHead={
          meetingData.letterHead ?? meetingData.agenda?.letterHead ?? undefined
        }
      />
    ).toBlob();
    const file = new File([blob], `Meeting Minutes ${meetingDate}.pdf`, {
      type: blob.type,
    });

    setMinutesPDFFile(file);
    setMeetingMinutesDocumentOpen(true);
  });

  const handleBack = useFutureCallback(() => {
    if (isEditing) {
      setEditing(false);
    } else {
      navigate("..");
    }
  });

  if (
    !tenantData ||
    !meetingData ||
    !teamData ||
    !userList ||
    !userMap ||
    !user
  )
    return null;

  return (
    <div className="flex flex-col flex-grow">
      {/* Run Meeting Loader */}

      {loadingRunMeeting && (
        <div className="fixed bg-black/25 flex flex-col justify-center items-center gap-y-4 z-30 inset-0">
          <Spinner />
          <p>Preparing items for running meeting...</p>
        </div>
      )}

      {/* Page Header */}

      <PageHeader title={`${tenantData.name}: ${meetingData.name}`}>
        {" "}
      </PageHeader>

      {/* Dialogs */}

      <InformationDialog
        title="Warning"
        message={editMeeting24hoursWarning}
        onConfirm={handleEdit}
        open={warningMeetingDialogOpen}
        setOpen={setWarningMeetingDialogOpen}
      />
      <PublishConfirmation
        key="publish"
        open={publishMeetingDialogOpen}
        setOpen={setPublishMeetingDialogOpen}
        itemId={meetingId ?? ""}
        onConfirm={handlePublish}
        pastMeetingWarning={meetingHasMinutes}
      />
      {!!errors && (
        <ErrorsDialog
          title={errorDialogTitle}
          errors={errors}
          open={errorsDialogOpen}
          setOpen={setErrorsDialogOpen}
        />
      )}
      <MeetingMinutesDialog
        key={minutesKey}
        meeting={meetingData}
        minutes={minutesData}
        open={editMinutesOpen}
        setOpen={setEditMinutesOpen}
      />
      <MeetingGenerateMinutesDialog
        minutesOrdering={tenantData.minutesOrdering}
        onConfirm={(data) => handleOpenMeetingMinutes(data.minutesOrdering)}
        open={minutesWarningOpen}
        setOpen={setMinutesWarningOpen}
      />
      <MeetingMinutesPreviewDialog
        tenantId={tenantData.id}
        meeting={meetingData}
        open={previewMinutesOpen}
        setOpen={setPreviewMinutesOpen}
        handleCreateDocument={handleCreateDocumentFromMinutes}
      />
      {meetingMinutesAIOpen && tenantData.featureFlags?.aiMeetingMinutes && (
        <MeetingMinutesAIDialog
          tenantId={tenantData.id}
          meetingId={meetingData.id}
          open={meetingMinutesAIOpen}
          setOpen={setMeetingMinutesAIOpen}
          onEnhanced={(minutesDeltaJson) => {
            const delta = JSON.parse(minutesDeltaJson) as Delta;
            setMinutesData(delta);
            setEditMinutesOpen(true);
            setMeetingMinutesAIOpen(false);
          }}
        />
      )}
      {/* Dialog for attaching meeting minutes to new document */}
      <CreateDocumentDialog
        tenantId={tenantId ?? ""}
        initialData={{
          name: minutesPDFFile?.name.replace(/\.pdf$/i, ""),
          uploadType: "meeting-minutes",
          minutesData: {
            meetingId: meetingData.id,
            revision: (meetingData.minutesData?.latestRevision ?? 0) + 1,
            official: false,
          },
        }}
        initialUpload={minutesPDFFile}
        open={meetingMinutesDocumentOpen}
        setOpen={setMeetingMinutesDocumentOpen}
        onComplete={async (doc) => {
          // reference the new document from the meeting to help user avoid creating duplicate documents
          if (!meetingData?.minutesData) return;

          await meetingRepo.update(tenantId, meetingData.id, {
            minutesData: {
              ...meetingData.minutesData,
              documentId: doc.id,
              latestRevision:
                doc.minutesData?.revision ??
                meetingData.minutesData.latestRevision,
            },
          });
        }}
      />

      <MeetingPreviewDialog
        allowShowDetails={canRunMeeting}
        title={meetingData.name}
        open={previewPDFOpen}
        setOpen={setPreviewPDFOpen}
        meeting={meetingData}
        team={teamData}
        tenant={tenantData}
        userMap={userMap}
      />

      {previewPublishedPDFOpen && (
        <DocumentPreviewer
          title={meetingData.name}
          open={previewPublishedPDFOpen}
          setOpen={setPreviewPublishedPDFOpen}
          documentUri={meetingData.agendaUrl}
        />
      )}

      <MeetingRunModeDialog
        open={meetingRunModeOpen}
        setOpen={setMeetingRunModeOpen}
        onConfirm={(mode) => handleStartRunMeeting(mode)}
      />

      <ConfirmDialog
        open={confirmDeleteMinutesOpen}
        setOpen={setConfirmDeleteMinutesOpen}
        cancelVariant="default"
        confirmLabel="Delete Minutes"
        confirmVariant="destructive"
        message={
          <p>
            Are you sure you want to delete the meeting minutes contents? You
            will be able to re-generate them, but if you've made any
            enhancements, or manual edits, all these will be lost.
            <br />
            <br />
            <strong>Note:</strong> Deleting the meeting minutes is the only way
            that will allow you to re-run the meeting.
          </p>
        }
        onSubmit={async () => {
          if (!meetingData?.minutesData) return;

          await meetingRepo.update(tenantId, meetingData.id, {
            minutesData: null,
          });
        }}
      />

      {/* Button Row */}

      <div className="mt-4 mb-6 flex flex-row flex-wrap justify-between items-center gap-2">
        <Button
          tooltip={
            teamMismatch
              ? "You are in the wrong team to be editing this meeting"
              : "Edit Meeting"
          }
          disabled={viewOnly}
          onClick={handleEditCheck}
        >
          {isEditing ? "Close Editing" : "Edit"}
        </Button>

        <Button
          disabled={loading}
          tooltip="View Latest Unpublished PDF"
          onClick={() => setPreviewPDFOpen(true)}
        >
          <EyeIcon className="w-5 -ml-1 mr-2" /> Preview
        </Button>

        <Button
          disabled={!meetingData.agendaUrl || loading}
          tooltip={
            meetingData.agendaUrl
              ? "View Public PDF"
              : "Agenda Not Published Yet"
          }
          onClick={() => setPreviewPublishedPDFOpen(true)}
        >
          <EyeIcon className="w-5 -ml-1 mr-2" /> View Published
        </Button>

        <div className="flex-grow" />

        <Button
          disabled={!hasMinRole("edit") || teamMismatch || loading}
          tooltip={
            !hasMinRole("edit") || teamMismatch
              ? "You must have edit authority to publish"
              : meetingHasMinutes
                ? "Warning: This will re-publish a past meeting"
                : "Publish meeting to the public"
          }
          onClick={handleOpenPublish}
        >
          Publish
        </Button>

        <Button
          tooltip={
            meetingData.minutesData ? (
              <>
                Meeting minutes were generated.
                <br />
                Delete the generated minutes in order to re-run the meeting.
              </>
            ) : canRunMeeting ? (
              "Act as secretary for this meeting"
            ) : (
              "You must be an editor or voting member to run this meeting"
            )
          }
          disabled={
            !canRunMeeting ||
            !!meetingData.minutesData ||
            teamMismatch ||
            loading
          }
          variant="tertiary"
          onClick={handleRunMeeting}
        >
          Run Meeting
          <PlayIcon size={16} className="ml-2" />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button>
              Minutes <ChevronDownIcon size={16} className="ml-1 -mr-1" />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end">
            {meetingData.minutesData ? (
              <DropdownMenuItem
                tooltip={
                  hasMinRole("edit")
                    ? "Edit Meeting Minutes"
                    : "You must be an editor to edit meeting minutes"
                }
                disabled={!hasMinRole("edit") || teamMismatch || loading}
                onClick={() => handleOpenMeetingMinutes()}
              >
                Edit Minutes
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem
                tooltip={
                  !hasMinRole("edit")
                    ? "You must be an editor to generate minutes"
                    : meetingData.review
                      ? "Generate meeting minutes"
                      : "Meeting must be run to generate minutes"
                }
                disabled={
                  !hasMinRole("edit") ||
                  !meetingData.review ||
                  teamMismatch ||
                  loading
                }
                onClick={() => setMinutesWarningOpen(true)}
              >
                Generate Minutes
              </DropdownMenuItem>
            )}

            {tenantData.featureFlags?.aiMeetingMinutes && (
              <DropdownMenuItem
                tooltip={
                  !hasMinRole("edit")
                    ? "You must be an editor to enhance minutes"
                    : meetingData.minutesData
                      ? "Enhance minutes automatically with AI"
                      : "Generate and save minutes first"
                }
                disabled={
                  !hasMinRole("edit") ||
                  !meetingData.minutesData ||
                  teamMismatch ||
                  loading
                }
                onClick={() => setMeetingMinutesAIOpen(true)}
              >
                Enhance Minutes With AI
              </DropdownMenuItem>
            )}

            <DropdownMenuItem
              tooltip={
                meetingData.minutesData
                  ? "View Meeting Minutes"
                  : "Meeting minutes have not been generated yet"
              }
              disabled={!meetingData.minutesData || teamMismatch || loading}
              onClick={() => setPreviewMinutesOpen(true)}
            >
              Preview Minutes
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              tooltip={
                !hasMinRole("edit")
                  ? "You must be an editor to delete minutes"
                  : "Clear minutes editor contents and start over"
              }
              disabled={
                !hasMinRole("edit") ||
                !meetingData.minutesData ||
                teamMismatch ||
                loading
              }
              onClick={() => setConfirmDeleteMinutesOpen(true)}
            >
              Delete Generated Minutes
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Editor */}

      <MeetingEditor
        viewOnly={!isEditing}
        tenant={tenantData}
        meeting={meetingData}
        canViewConfidential={canViewConfidential}
        canShowDetails={canRunMeeting}
        team={teamData}
        user={user}
        userMap={userMap}
        onBack={handleBack}
      />
    </div>
  );
};
